// ----- Wave E.2 — LifecycleTab ----- //
//
// Cross-sequence history of a document, rendered as a vertical timeline.
// One fetch on tab activation via v2.documents.getLifecycle(). Per
// design spec §2 Q8 this is ONE call (not V1's N+1).
//
// V2 doesn't yet have a `LineageId` column on Documents — the endpoint
// returns `lineageTracked: false` and a single-entry history. When
// LineageId lands, this component will render the full timeline
// without any caller change (it just iterates `entries`).

import { useEffect, useState, type ReactElement } from 'react';
import { ApiError, v2, type DocumentLifecycle } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';

export interface LifecycleTabProps {
  readonly submissionId: string;
  readonly documentId: string;
  /**
   * Bump to force a re-fetch of the history WITHOUT remounting — e.g. after a
   * lifecycle operation is saved, so the newly-recorded entry appears instead of
   * a stale timeline (Kimi F3). Unchanged across ordinary tab switches.
   */
  readonly refreshNonce?: number;
}

const ERROR_MESSAGES: Record<string, string> = {
  NotFound: 'This document no longer exists.',
  PermissionDenied: 'You do not have permission to view lifecycle history.',
};

export function LifecycleTab({
  submissionId,
  documentId,
  refreshNonce,
}: LifecycleTabProps): ReactElement {
  const [data, setData] = useState<DocumentLifecycle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    v2.documents
      .getLifecycle(submissionId, documentId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(formatApiError(err, 'Could not load lifecycle.', ERROR_MESSAGES));
        if (!(err instanceof ApiError)) {
          globalThis.console.error('[LifecycleTab] unexpected error', err);
        }
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, documentId, refreshNonce]);

  if (loading) {
    return (
      <div data-testid="lifecycle-loading" style={{ padding: 12, fontSize: 12, color: '#6B7280' }}>
        Loading lifecycle…
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="lifecycle-error"
        role="alert"
        style={{
          padding: 10,
          background: '#FEE2E2',
          color: '#B91C1C',
          border: '1px solid #B91C1C',
          borderRadius: 4,
          fontSize: 12,
        }}
      >
        {error}
      </div>
    );
  }

  if (!data) return <></>;

  const onlyOne = data.entries.length <= 1;

  return (
    <div data-testid="lifecycle-tab" style={{ padding: '8px 4px' }}>
      {onlyOne && !data.lineageTracked && (
        <div
          data-testid="lifecycle-single-entry-explainer"
          style={{
            fontSize: 11,
            color: '#6B7280',
            fontStyle: 'italic',
            marginBottom: 10,
          }}
        >
          Cross-sequence lineage tracking isn't yet enabled. Showing the
          current sequence only.
        </div>
      )}

      {data.entries.length === 0 ? (
        <div
          data-testid="lifecycle-empty"
          style={{ padding: 12, fontSize: 12, color: '#6B7280', fontStyle: 'italic' }}
        >
          This is the first occurrence of this document in the submission
          lifecycle.
        </div>
      ) : (
        <ol
          data-testid="lifecycle-timeline"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          {data.entries.map((entry) => (
            <li
              key={`${entry.sequenceNumber}-${entry.documentId}`}
              data-testid={`lifecycle-entry-${entry.sequenceNumber}`}
              style={{
                padding: '8px 0',
                borderLeft: '2px solid #E5E7EB',
                paddingLeft: 12,
                marginLeft: 4,
                marginBottom: 4,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>
                Seq {entry.sequenceNumber}{' '}
                <span
                  data-testid={`lifecycle-entry-${entry.sequenceNumber}-op`}
                  style={{
                    fontSize: 10,
                    background: '#E5E7EB',
                    padding: '1px 6px',
                    borderRadius: 3,
                    marginLeft: 6,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  {entry.operation}
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                {entry.approvedAt
                  ? new Date(entry.approvedAt).toISOString().slice(0, 10)
                  : '(not yet approved)'}
                {entry.approvedBy ? ` · ${entry.approvedBy}` : ''}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
